import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { PermissionsService } from '../services/permission.service';
import { TenantStateService } from '../shared/services/tenant.state.service';
import { TenantService } from '../tenant/tenant.service';
import { switchMap, take } from 'rxjs/operators';
@Component({
    selector: 'app-auth-callback',
    templateUrl: './auth-callback.component.html',
    styleUrls: ['./auth-callback.component.css'],
    standalone: false
})

export class AuthCallbackComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject();
  private static readonly AUTH_RETRY_KEY = 'authCallbackRetry';
  private static readonly STALL_TIMEOUT_MS = 20_000;
  statusMessage = '@authAuthenticating';
  stalled = false;
  private stallTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private authService: AuthService,
    private tenantStateService: TenantStateService,
    private tenantService: TenantService,
    private permissionsService: PermissionsService) { }

  ngOnDestroy(): void {
    this.clearStallTimer();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    // Per-step budget: re-armed at each checkpoint so a single stuck step trips it, not total elapsed.
    this.armStallTimer();

    this.authService.completeAuthentication().then(() => {
      this.armStallTimer();
      sessionStorage.removeItem(AuthCallbackComponent.AUTH_RETRY_KEY);
      this.statusMessage = '@authInitializingState';

      this.tenantService.getData().pipe(
        take(1),
        switchMap(tenants => this.tenantStateService.populateStore(tenants))
      ).subscribe({
        next: () => {
          this.armStallTimer();
          this.statusMessage = '@authRedirecting';
          this.permissionsService.navigateAfterLogin();
        },
        error: err => console.error('Auth-callback post-auth pipeline failed', err),
      });
    },
    error => {
      console.error(error);
      const retryCount = parseInt(sessionStorage.getItem(AuthCallbackComponent.AUTH_RETRY_KEY) || '0', 10);
      if (retryCount < 1) {
        sessionStorage.setItem(AuthCallbackComponent.AUTH_RETRY_KEY, String(retryCount + 1));
        this.authService.startAuthentication();
      } else {
        sessionStorage.removeItem(AuthCallbackComponent.AUTH_RETRY_KEY);
        this.statusMessage = '@authFailed';
        this.stalled = true;
        this.clearStallTimer();
      }
    });
  }

  retry(): void {
    sessionStorage.removeItem(AuthCallbackComponent.AUTH_RETRY_KEY);
    this.authService.startAuthentication();
  }

  signOut(): void {
    this.authService.logout();
  }

  private armStallTimer(): void {
    this.clearStallTimer();
    this.stallTimer = setTimeout(() => {
      this.stalled = true;
      this.statusMessage = '@authStalled';
    }, AuthCallbackComponent.STALL_TIMEOUT_MS);
  }

  private clearStallTimer(): void {
    if (this.stallTimer) {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
  }
}
